import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';
import { ErrorBoundary } from 'react-error-boundary';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

const ErrorFallback = ({ error, resetErrorBoundary }) => {
    return (
        <div className="p-4 bg-red-500/10 rounded-lg">
            <h2 className="text-xl font-bold mb-2">Something went wrong:</h2>
            <pre className="text-sm text-red-200">{error.message}</pre>
            <button 
                onClick={resetErrorBoundary}
                className="mt-4 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
            >
                Try again
            </button>
        </div>
    );
};

const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

const REGION_CONFIG = {
    SE: {
        regions: ['SE1', 'SE2', 'SE3', 'SE4'],
        colors: {
            'SE1': '#e5e7eb',
            'SE2': '#9ca3af',
            'SE3': '#6b7280',
            'SE4': '#4b5563'
        },
        unit: 'öre/kWh'
    },
    NO: {
        regions: ['NO1', 'NO2', 'NO3', 'NO4', 'NO5'],
        colors: {
            'NO1': '#60a5fa',
            'NO2': '#3b82f6',
            'NO3': '#2563eb',
            'NO4': '#1d4ed8',
            'NO5': '#1e40af'
        },
        unit: 'NOK/kWh'
    }
};

const HistoricalPricingContent = () => {
    const [historicalData, setHistoricalData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedCountry, setSelectedCountry] = useState('SE');
    const [selectedRegions, setSelectedRegions] = useState(['SE1', 'SE2', 'SE3', 'SE4']);
    const [selectedTimeframe, setSelectedTimeframe] = useState('year');

    const countryConfig = REGION_CONFIG[selectedCountry];
    const regionColors = countryConfig.colors;
    const allRegions = countryConfig.regions;
    const unit = countryConfig.unit;

    useEffect(() => {
        setSelectedRegions(REGION_CONFIG[selectedCountry].regions);
        setHistoricalData(null);

        const storageKey = `historical_pricing_data_${selectedCountry}`;

        const fetchHistoricalData = async () => {
            try {
                setLoading(true);

                // Check localStorage first
                const cached = localStorage.getItem(storageKey);
                if (cached) {
                    const { data, timestamp } = JSON.parse(cached);
                    if (Date.now() - timestamp < CACHE_DURATION) {
                        setHistoricalData(data);
                        setLoading(false);
                        return;
                    }
                }

                const currentYear = new Date().getFullYear();
                const startYear = currentYear - 4; // Get last 5 years
                const response = await axios.get(`/api/historical-prices?startYear=${startYear}&endYear=${currentYear}&country=${selectedCountry}`);
                if (response.data && Object.keys(response.data).length > 0) {
                    // Store in localStorage with timestamp
                    localStorage.setItem(storageKey, JSON.stringify({
                        data: response.data,
                        timestamp: Date.now()
                    }));

                    setHistoricalData(response.data);
                    setError(null);
                } else {
                    setError('No historical data available');
                }
            } catch (err) {
                setError('Failed to load historical data');
                console.error('Historical data fetch error:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchHistoricalData();
    }, [selectedCountry]);

    const processedData = useMemo(() => {
        if (!historicalData || Object.keys(historicalData).length === 0) return null;

        const data = {
            labels: [],
            datasets: []
        };

        // Show selected regions or all regions for current country
        const regionsToShow = selectedRegions.length > 0 ? selectedRegions : allRegions;

        // Create datasets for each region
        regionsToShow.forEach(region => {
            if (regionColors[region]) {
                data.datasets.push({
                    label: region,
                    data: [],
                    borderColor: regionColors[region],
                    backgroundColor: `${regionColors[region]}25`,
                    fill: false,
                    tension: 0.2,
                    borderWidth: 3,
                    pointRadius: 3,
                    pointHoverRadius: 6,
                    pointBackgroundColor: regionColors[region],
                    pointBorderColor: '#ffffff',
                    pointBorderWidth: 1,
                    spanGaps: true // This will connect points even when there are null values
                });
            }
        });

        // Populate data points
        if (selectedTimeframe === 'year') {
            // Yearly data processing
            Object.entries(historicalData)
                .sort(([yearA], [yearB]) => yearA - yearB)
                .forEach(([year, yearData]) => {
                    // Yearly average - add year label once
                    if (!data.labels.includes(year)) {
                        data.labels.push(year);
                    }
                    
                    // Add data for each region
                    regionsToShow.forEach(region => {
                        const datasetIndex = data.datasets.findIndex(ds => ds.label === region);
                        if (datasetIndex !== -1 && yearData[region]) {
                            const yearlyAvg = calculateYearlyAverage({ [year]: yearData }, region);
                            data.datasets[datasetIndex].data.push(parseFloat(yearlyAvg) || null);
                        } else if (datasetIndex !== -1) {
                            data.datasets[datasetIndex].data.push(null);
                        }
                    });
                });
        } else {
            // Monthly data processing - collect ALL monthly data from ALL years first
            const monthlyData = new Map();
            
            Object.entries(historicalData)
                .sort(([yearA], [yearB]) => yearA - yearB)
                .forEach(([year, yearData]) => {
                    Object.entries(yearData).forEach(([region, regionData]) => {
                        if (regionsToShow.includes(region) && regionData.monthlyAverages) {
                            Object.entries(regionData.monthlyAverages).forEach(([month, average]) => {
                                const label = `${year}-${month.padStart(2, '0')}`;
                                if (!monthlyData.has(label)) {
                                    monthlyData.set(label, new Map());
                                }
                                monthlyData.get(label).set(region, parseFloat(average));
                            });
                        }
                    });
                });
            
            // Get all unique month labels sorted
            let sortedLabels = Array.from(monthlyData.keys()).sort();

            // Filter to last 6 months if that timeframe is selected
            if (selectedTimeframe === '6months') {
                sortedLabels = sortedLabels.slice(-6);
            }

            data.labels = sortedLabels;
            
            // For each dataset (region), ensure it has data for every label
            data.datasets.forEach(dataset => {
                const regionName = dataset.label;
                dataset.data = sortedLabels.map(label => {
                    const monthData = monthlyData.get(label);
                    return monthData ? (monthData.get(regionName) || null) : null;
                });
            });
        }

        return data;
    }, [historicalData, selectedRegions, selectedTimeframe, regionColors, allRegions]);

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        animation: {
            duration: 750,
            easing: 'easeInOutQuart'
        },
        interaction: {
            intersect: false,
            mode: 'index'
        },
        plugins: {
            tooltip: {
                backgroundColor: 'rgba(0, 0, 0, 0.85)',
                padding: 12,
                titleFont: { size: 14, weight: 'bold' },
                bodyFont: { size: 13 },
                borderColor: 'rgba(255, 255, 255, 0.1)',
                borderWidth: 1,
                callbacks: {
                    label: (context) => {
                        const value = context.raw;
                        return value !== null ? `${context.dataset.label}: ${value.toFixed(selectedCountry === 'NO' ? 3 : 2)} ${unit}` : `${context.dataset.label}: No data`;
                    }
                }
            },
            legend: {
                position: 'top',
                labels: {
                    color: '#d1d5db',
                    padding: 16,
                    font: { size: 12, weight: '500' },
                    usePointStyle: true,
                    pointStyle: 'circle',
                    boxWidth: 6
                }
            }
        },
        scales: {
            y: {
                border: { display: false },
                grid: {
                    color: 'rgba(156, 163, 175, 0.1)',
                    drawTicks: false
                },
                ticks: {
                    color: 'rgba(209, 213, 219, 0.8)',
                    font: { size: 11 },
                    padding: 8,
                    callback: (value) => selectedCountry === 'NO' ? `${value.toFixed(2)} NOK` : `${value.toFixed(0)} öre`
                }
            },
            x: {
                border: { display: false },
                grid: { display: false },
                ticks: {
                    color: 'rgba(209, 213, 219, 0.8)',
                    font: { size: 11 },
                    maxRotation: 45
                }
            }
        }
    };

    if (loading) {
        return (
            <div className="animate-pulse space-y-4">
                <div className="h-[400px] bg-white/5 rounded-lg"></div>
                <div className="grid grid-cols-4 gap-4">
                    {[...Array(4)].map((_, i) => (
                        <div key={i} className="h-[100px] bg-white/5 rounded-lg"></div>
                    ))}
                </div>
            </div>
        );
    }

    // Handle region selection
    const toggleRegion = (region) => {
        setSelectedRegions(prev => {
            if (prev.includes(region)) {
                return prev.filter(r => r !== region);
            } else {
                return [...prev, region];
            }
        });
    };

    const selectAllRegions = () => {
        setSelectedRegions(allRegions);
    };

    const clearAllRegions = () => {
        setSelectedRegions([]);
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-900 via-slate-900 to-slate-800 text-white" style={{background: 'linear-gradient(to bottom right, #0b0f18, #1f2937, #374151)'}}>
            <div className="container mx-auto px-4 py-8 max-w-7xl">
                {error && (
                    <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-200">
                        {error}
                    </div>
                )}

                {/* Header & Controls */}
                <div className="mb-8">
                    {/* Country Selector */}
                    <div className="flex gap-2 mb-4">
                        <button
                            onClick={() => setSelectedCountry('SE')}
                            className={`px-3 py-1.5 rounded-md text-sm transition-all ${
                                selectedCountry === 'SE'
                                    ? 'bg-blue-700 text-white'
                                    : 'bg-white/10 text-white/70 hover:bg-white/20'
                            }`}
                        >
                            Sverige (SE)
                        </button>
                        <button
                            onClick={() => setSelectedCountry('NO')}
                            className={`px-3 py-1.5 rounded-md text-sm transition-all ${
                                selectedCountry === 'NO'
                                    ? 'bg-blue-700 text-white'
                                    : 'bg-white/10 text-white/70 hover:bg-white/20'
                            }`}
                        >
                            Norge (NO)
                        </button>
                    </div>
                    {/* Timeframe Controls */}
                    <div className="flex gap-2 mb-6">
                        <button 
                            onClick={() => setSelectedTimeframe('year')}
                            className={`px-3 py-1.5 rounded-md text-sm transition-all ${
                                selectedTimeframe === 'year' 
                                    ? 'bg-gray-600 text-white' 
                                    : 'bg-white/10 text-white/70 hover:bg-white/20'
                            }`}
                        >
                            Årlig
                        </button>
                        <button 
                            onClick={() => setSelectedTimeframe('month')}
                            className={`px-3 py-1.5 rounded-md text-sm transition-all ${
                                selectedTimeframe === 'month' 
                                    ? 'bg-gray-600 text-white' 
                                    : 'bg-white/10 text-white/70 hover:bg-white/20'
                            }`}
                        >
                            Månatlig
                        </button>
                        <button 
                            onClick={() => setSelectedTimeframe('6months')}
                            className={`px-3 py-1.5 rounded-md text-sm transition-all ${
                                selectedTimeframe === '6months' 
                                    ? 'bg-gray-600 text-white' 
                                    : 'bg-white/10 text-white/70 hover:bg-white/20'
                            }`}
                        >
                            6 månader
                        </button>
                    </div>
                </div>

                {/* Price Cards */}
                <div className={`grid gap-3 mb-8 grid-cols-2 ${allRegions.length > 4 ? 'sm:grid-cols-5' : 'sm:grid-cols-4'}`}>
                    {allRegions.map((region) => {
                        const isSelected = selectedRegions.includes(region);
                        const color = regionColors[region];
                        const average = historicalData ? calculateYearlyAverage(historicalData, region) : '-';
                        
                        return (
                            <div
                                key={region}
                                onClick={() => toggleRegion(region)}
                                className={`
                                    p-4 rounded-lg cursor-pointer transition-all duration-200
                                    border-2 backdrop-blur-sm relative overflow-hidden
                                    ${
                                        isSelected 
                                            ? 'bg-white/15 border-white/40 shadow-xl ring-2 ring-white/20' 
                                            : 'bg-white/5 border-gray-600/50 hover:bg-white/10 hover:border-white/30 hover:shadow-lg'
                                    }
                                `}
                            >
                                <div className="flex items-center gap-3 mb-2">
                                    <div 
                                        className="w-4 h-4 rounded-full flex-shrink-0 border-2 border-white/20 shadow-sm"
                                        style={{ 
                                            backgroundColor: color,
                                            boxShadow: `0 0 8px ${color}40`
                                        }}
                                    />
                                    <span className="font-semibold text-sm tracking-wide">{region}</span>
                                </div>
                                <div className="text-sm font-medium text-white/80">
                                    {average !== '-' ? (
                                        <>
                                            <span className="text-white">{average}</span>
                                            <span className="text-white/60 ml-1">{unit}</span>
                                        </>
                                    ) : (
                                        <span className="text-white/50">Ingen data</span>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Chart */}
                <div className="bg-gray-800/30 backdrop-blur-sm border border-gray-600/20 rounded-xl p-6 mb-8">
                    <div className="h-[400px] lg:h-[500px]">
                        {loading ? (
                            <div className="flex items-center justify-center h-full">
                                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
                            </div>
                        ) : processedData ? (
                            <Line data={processedData} options={chartOptions} />
                        ) : (
                            <div className="flex items-center justify-center h-full text-white/50">
                                Ingen data tillgänglig
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const calculateYearlyAverage = (data, region) => {
    if (!data) return '-';
    let sum = 0;
    let count = 0;

    Object.values(data).forEach(yearData => {
        if (yearData[region] && yearData[region].monthlyAverages) {
            Object.values(yearData[region].monthlyAverages).forEach(average => {
                if (average) {
                    sum += parseFloat(average);
                    count++;
                }
            });
        }
    });

    return count > 0 ? (sum / count).toFixed(2) : '-';
};

const HistoricalPricing = () => {
    return (
        <ErrorBoundary
            FallbackComponent={ErrorFallback}
            onReset={() => window.location.reload()}
        >
            <HistoricalPricingContent />
        </ErrorBoundary>
    );
};

export default React.memo(HistoricalPricing);
